import { FC, useState } from 'react';

import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import groupBy from 'lodash/groupBy';
import { useTranslation } from 'react-i18next';

import { ContentReference } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';

import { COUNTRIES, getCountryLabel } from '@/utils/document-utils/countries';
import { sortDocumentsByTags } from '@/utils/formatDocumentTag';

import {
  ChooseDocumentCard,
  ChooseDocumentCountryTabs,
} from '@/components/document';
import PathwayModal from '@/components/popups/PathwayModal';

import { userPrefsAtom } from '@/atoms/userPrefs';

interface ChooseDocumentProps {
  icon?: string;
  isOpen: boolean;
  onClick: (document: ContentReference) => void;
  /** Leave the chooser without opening a version (returns to search when set). */
  onClose?: () => void;
  references: ContentReference[];
}

const ChooseDocumentModal: FC<ChooseDocumentProps> = ({
  icon = '',
  isOpen,
  onClick,
  onClose,
  references,
}) => {
  const { chooseDocumentVersion } = useFlags();
  const { t } = useTranslation();
  const userPrefs = useAtomValue(userPrefsAtom);
  const [chosenCountryTab, setChosenCountryTab] = useState<
    ContentReference['country'] | null
  >(null);
  const { data: auth } = useAuth();
  const isAdminOperatorOrStakeholder = auth?.isAdminOperatorOrStakeholder;

  const enableChooseDocumentVersion =
    chooseDocumentVersion && isAdminOperatorOrStakeholder;

  const referencesGroup = groupBy(references, 'country');
  const userCountryId = userPrefs.country.id;
  const activeCountryTab =
    chosenCountryTab ??
    (userCountryId && referencesGroup[userCountryId]
      ? userCountryId
      : COUNTRIES.find(({ id }) => referencesGroup[id]?.length)?.id);

  const countryHasResource = COUNTRIES.filter(
    ({ id }) => referencesGroup[id]?.length,
  ).map(({ id }) => id);

  const activeReferences = sortDocumentsByTags(
    activeCountryTab ? referencesGroup[activeCountryTab] : undefined,
  );

  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={onClose ?? (() => {})}
      showCloseButton={!!onClose}
      size="lg"
      stepKey={activeCountryTab}
    >
      <PathwayModal.Header>
        <PathwayModal.Title>{t('Generic.chooseVersion')}</PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body>
        <ChooseDocumentCountryTabs
          activeCountryTab={activeCountryTab}
          countryHasResource={countryHasResource}
          onCountryTabChange={setChosenCountryTab}
        />
        <div>
          {!!activeReferences?.length &&
            [...activeReferences].map((document, index) => (
              <ChooseDocumentCard
                icon={icon}
                key={`${document.country}_${index}`}
                name={document.name ?? ''}
                onClick={() => onClick(document)}
                tags={document.tags}
                country={
                  enableChooseDocumentVersion
                    ? getCountryLabel(document.country as string)
                    : ''
                }
              />
            ))}
        </div>
      </PathwayModal.Body>
    </PathwayModal>
  );
};

export default ChooseDocumentModal;
